import { useState } from 'react';
import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { inviteChatMembers } from 'flow/actions/chat-actions';
import { ChatSessionKey } from 'flow/types/EntityKeys';

type Props = {
    guestName: string;
    chatSessionKey: ChatSessionKey;
    chatUserKey: string | undefined;
    onClose: () => void;
};

/**
 * Warns before granting a live guest permanent room access: once added,
 * they're a real chat-service member — full history and files, not just
 * this call — so this can't be a single accidental click.
 */
const AddGuestToRoomModal = ({
    guestName,
    chatSessionKey,
    chatUserKey,
    onClose,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = () => {
        if (!chatUserKey || submitting) return;
        setSubmitting(true);
        dispatch(
            inviteChatMembers(chatSessionKey, [{ key: chatUserKey }], {
                onSuccess: () => {
                    setSubmitting(false);
                    onClose();
                },
                // networkErrorDispatcher (inviteChatMembers' own onError)
                // already shows the error snackbar — this just lets the
                // guest try again instead of the modal silently closing on
                // a failed invite.
                onError: () => setSubmitting(false),
            }),
        );
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('meetings.participants.add_to_room_title')}
            modalSize={modalSizes.SMALL}
            confirmButtonText={i18next.t(
                'meetings.participants.add_to_room_confirm',
            )}
            disableSubmit={!chatUserKey || submitting}
            onSubmit={handleSubmit}
            onClose={onClose}
        >
            <p className="text-sm text-gray-600">
                {i18next.t('meetings.participants.add_to_room_warning', {
                    name: guestName,
                })}
            </p>
        </BaseModal>
    );
};

export default AddGuestToRoomModal;
